/**
 * LiveWinsTicker.jsx — landing-page ticker fed by /api/live-wins
 *
 * - Fetches 50 simulated wins once on mount.
 * - Auto-refreshes when the returned `expires_at` (next 00:00 UTC) is hit.
 * - Clicking a win triggers `onItemClick(item)` so the parent can route.
 *   Guests → /signup, logged-in users → /lobby?launch=<game_id>.
 * - Whale wins (>100 SC) are visually accented (emerald gradient text +
 *   subtle glow), matching the backend's `is_whale` flag.
 *
 * Falls back to a static 10-row seed pool if the API is unreachable so
 * the marquee never disappears from the landing page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Trophy } from "lucide-react";
import { API } from "../App";

const FALLBACK_ITEMS = [
  // 3 Oaks + Spinjoy removed from fallback pool (Feb 2026) — those providers
  // are now admin-only so we don't tease unreachable games in the ticker.
  { user: "@HighRoller24", amount_sc: 420.0,  game_name: "Wildlife Riches",    game_id: "wildlife_riches",   provider: "booming", is_whale: true },
  { user: "@JackpotJake",  amount_sc:  67.85, game_name: "Book of Amaterasu",  game_id: "book_of_amaterasu", provider: "booming", is_whale: false },
  { user: "@RoyalRaven",   amount_sc:  95.4,  game_name: "Bastet & Cats",      game_id: "bastet_and_cats",   provider: "booming", is_whale: false },
  { user: "@NeonQueen",    amount_sc: 375.4,  game_name: "Fire Hit 2",         game_id: "FireHit2",          provider: "ka_gaming", is_whale: true },
  { user: "@LuckyDucky88", amount_sc: 189.75, game_name: "Skybound Pirates",   game_id: "SkyboundPirates",   provider: "ka_gaming", is_whale: true },
  { user: "@GoldFever",    amount_sc: 115.2,  game_name: "Big Bounty Bandits 3 Pots", game_id: "big_bounty_bandits", provider: "petersons", is_whale: true },
  { user: "@DiamondRush",  amount_sc: 754.0,  game_name: "The Game Show",      game_id: "the_game_show",     provider: "betsoft", is_whale: true },
  { user: "@MoneyMaven",   amount_sc: 320.5,  game_name: "Bloody Mary",        game_id: "bloody_mary",       provider: "petersons", is_whale: true },
  { user: "@WhaleHunter",  amount_sc: 245.5,  game_name: "Good Girl Bad Girl 2", game_id: "good_girl_bad_girl_2", provider: "betsoft", is_whale: true },
  { user: "@SpinMaster99", amount_sc: 520.0,  game_name: "Greedy Alice 2",     game_id: "greedy_alice_2",    provider: "petersons", is_whale: true },
];

const formatSC = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function LiveWinsTicker({ onItemClick }) {
  const [items, setItems]         = useState(FALLBACK_ITEMS);
  const [expiresAt, setExpiresAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/live-wins`, { timeout: 8000 });
      if (r.data?.items?.length) {
        setItems(r.data.items);
        setExpiresAt(r.data.expires_at || null);
      }
    } catch (_err) {
      // Fallback pool already loaded — silent.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh at the next 00:00 UTC window without hammering the API.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
    if (msUntilExpiry <= 0) return undefined;
    const t = setTimeout(load, msUntilExpiry + 5000);
    return () => clearTimeout(t);
  }, [expiresAt, load]);

  // Duplicate the list so the marquee has enough length to scroll seamlessly.
  const marqueeItems = useMemo(() => items.concat(items), [items]);

  return (
    <div
      className="fixed top-16 w-full z-40 bg-gradient-to-r from-amber-500/[0.08] via-amber-400/[0.12] to-amber-500/[0.08] border-y border-amber-400/30 backdrop-blur-md overflow-hidden"
      data-testid="live-wins-ticker"
    >
      <div className="flex items-center h-9">
        <div className="flex-shrink-0 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 font-black text-[10px] tracking-widest uppercase">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 mr-1.5 animate-pulse" />
          LIVE WINS
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div className="jr-marquee flex gap-6 whitespace-nowrap text-xs font-medium text-amber-100/90">
            {marqueeItems.map((it, i) => (
              <button
                key={`${it.user}-${it.game_id}-${i}`}
                type="button"
                onClick={() => onItemClick?.(it)}
                data-testid={`live-win-item-${i}`}
                data-game-id={it.game_id}
                className={`inline-flex items-center gap-2 px-2 py-1 rounded-md transition-colors bg-transparent border-0 cursor-pointer hover:bg-amber-400/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-300 ${
                  it.is_whale ? "shadow-[inset_0_0_0_1px_rgba(52,211,153,0.15)]" : ""
                }`}
                title={`Play ${it.game_name}`}
              >
                <Trophy className={`w-3 h-3 ${it.is_whale ? "text-emerald-300" : "text-amber-300"}`} />
                <span className="text-amber-200/80">{it.user}</span>
                <span className={`font-bold ${it.is_whale ? "text-emerald-300 drop-shadow-[0_0_6px_rgba(52,211,153,0.6)]" : "text-emerald-400"}`}>
                  won {formatSC(it.amount_sc)} SC
                </span>
                <span className="text-slate-400">on</span>
                <span className="text-white font-semibold">{it.game_name}</span>
                <span className="text-amber-400/40">•</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
